import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  TextField,
  Autocomplete,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  PrimeExoneree,
  PrimeNonExoneree,
  CotisationSociale,
  EmployeePrimeExoneree,
  EmployeePrimeNonExoneree,
  EmployeeCotisationSociale,
  calculatePayroll,
  tauxAncienneteAt,
  primeAppliesToMonth,
  PayrollCalculation,
} from '../../../types/paie';
import {
  primesExonereesAPI,
  primesNonExonereesAPI,
  cotisationsSocialesAPI,
  employeePrimesAPI,
} from '../../../services/api';
import PriceInput from '../../../components/PriceInput';

// Every prime row sits on the same tinted surface: a one-shot prime is two
// lines tall and needs the block to read as one row, and giving only those rows
// a background made the ordinary ones look unfinished next to them.
const PRIME_ROW_SX = {
  p: 1,
  borderRadius: 1,
  bgcolor: 'action.hover',
} as const;

// Le mois courant, au format que porte la colonne DATE (premier du mois : la
// période se compare au mois, jamais au jour).
const currentMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Fin du mois choisi : désactiver une prime l'arrête à la fin du mois en cours,
// les mois déjà payés restent acquis.
const endOfMonthISO = (iso?: string | null) => {
  const d = iso ? new Date(iso) : new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
};

const monthInput = (iso?: string | null) => (iso ? iso.slice(0, 7) : '');

const MOIS_NOMS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const HR_COLOR = '#00897B';

interface PrimeRowExoneree {
  id: string;
  // Row id in the database, when this line already exists. Sent back on save so
  // the server updates it in place and the validity period survives.
  db_id?: number | null;
  date_debut?: string | null;
  date_fin?: string | null; // temp ID for UI
  prime_id: number | null;
  prime_nom: string;
  montant: number;
  // Read-only here — the fiche has no period selector for an exonérée, but the
  // récapitulatif needs them to decide whether the prime is live this month.
  one_shot?: boolean;
  mois?: number | null;
  annee?: number | null;
}

interface PrimeRowNonExoneree {
  id: string;
  // Row id in the database, when this line already exists. Sent back on save so
  // the server updates it in place and the validity period survives.
  db_id?: number | null;
  date_debut?: string | null;
  date_fin?: string | null;
  prime_id: number | null;
  prime_nom: string;
  montant: number;
  // Day-based primes only (prime.base_jours): what HR typed, and what the
  // montant below was derived from.
  nombre_jours?: number | null;
  // One-shot primes only: the month THIS employee is paid in. Held per row so
  // two employees — or the same one twice — can be paid in different months
  // from a single prime definition.
  mois?: number | null;
  annee?: number | null;
  one_shot?: boolean;
}

interface CotisationRow {
  id: string;
  cotisation_id: number | null;
  cotisation_nom: string;
  cotisation_taux: number;
}

interface PaieTabProps {
  employeeId: number | null;
  dateEmbauche: string | null;
  salaireBase: number | null;
  declarationJournaliere: number | null;
  nbrEnfants: number;
  situationFamiliale: string;
  isReadOnly: boolean;
  onSalaireChange: (field: string, value: any) => void;
  onPrimesChange: (
    primesExonerees: { id?: number | null; prime_id: number; montant: number; date_debut?: string | null; date_fin?: string | null }[],
    primesNonExonerees: { id?: number | null; prime_id: number; montant: number; nombre_jours?: number | null; mois?: number | null; annee?: number | null; date_debut?: string | null; date_fin?: string | null }[],
    cotisationsSociales: { cotisation_id: number }[]
  ) => void;
  hasError: (field: string) => boolean;
  getErrorText: (field: string) => string;
}

const PaieTab: React.FC<PaieTabProps> = ({
  employeeId,
  dateEmbauche,
  salaireBase,
  declarationJournaliere,
  nbrEnfants,
  situationFamiliale,
  isReadOnly,
  onSalaireChange,
  onPrimesChange,
  hasError,
  getErrorText,
}) => {
  // Config data (dropdowns)
  const [availablePrimesExonerees, setAvailablePrimesExonerees] = useState<PrimeExoneree[]>([]);
  const [availablePrimesNonExonerees, setAvailablePrimesNonExonerees] = useState<PrimeNonExoneree[]>([]);
  const [cotisations, setCotisations] = useState<CotisationSociale[]>([]);

  // Employee primes and cotisations
  const [primesExonerees, setPrimesExonerees] = useState<PrimeRowExoneree[]>([]);
  const [primesNonExonerees, setPrimesNonExonerees] = useState<PrimeRowNonExoneree[]>([]);
  const [employeeCotisations, setEmployeeCotisations] = useState<CotisationRow[]>([]);
  const [availableCotisations, setAvailableCotisations] = useState<CotisationSociale[]>([]);

  // Loading states
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingPrimes, setLoadingPrimes] = useState(false);

  // Payroll calculation
  const [payroll, setPayroll] = useState<PayrollCalculation | null>(null);

  // Generate unique ID for new rows
  const generateId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Load config data on mount
  useEffect(() => {
    const loadConfig = async () => {
      setLoadingConfig(true);
      try {
        const [primesExRes, primesNonExRes, cotisationsRes] = await Promise.all([
          primesExonereesAPI.getAll(),
          primesNonExonereesAPI.getAll(),
          cotisationsSocialesAPI.getAll(),
        ]);
        setAvailablePrimesExonerees(primesExRes.items.filter(p => p.is_active));
        setAvailablePrimesNonExonerees(primesNonExRes.items.filter(p => p.is_active));
        const activeCotisations = cotisationsRes.items.filter(c => c.is_active);
        setCotisations(activeCotisations);
        setAvailableCotisations(activeCotisations);
      } catch (error) {
        console.error('Error loading PAIE config:', error);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);

  // Load employee primes when employeeId changes
  useEffect(() => {
    const loadEmployeePrimes = async () => {
      if (!employeeId) {
        // New employee - start with empty
        setPrimesExonerees([]);
        setPrimesNonExonerees([]);
        setEmployeeCotisations([]);
        return;
      }

      setLoadingPrimes(true);
      try {
        const response = await employeePrimesAPI.get(employeeId);

        // Map to row format
        setPrimesExonerees(
          response.primes_exonerees.map((p: EmployeePrimeExoneree) => ({
            id: `existing-${p.id}`,
            db_id: p.id,
            prime_id: p.prime_id,
            prime_nom: p.prime_nom || '',
            montant: p.montant,
            date_debut: p.date_debut ?? null,
            date_fin: p.date_fin ?? null,
            one_shot: p.one_shot ?? false,
            mois: p.mois ?? p.prime_mois ?? null,
            annee: p.annee ?? p.prime_annee ?? null,
          }))
        );
        setPrimesNonExonerees(
          response.primes_non_exonerees.map((p: EmployeePrimeNonExoneree) => ({
            id: `existing-${p.id}`,
            db_id: p.id,
            prime_id: p.prime_id,
            prime_nom: p.prime_nom || '',
            montant: p.montant,
            date_debut: p.date_debut ?? null,
            date_fin: p.date_fin ?? null,
            nombre_jours: p.nombre_jours ?? null,
            // Assignment period first, then the definition's as the legacy
            // fallback, then today — a one-shot row left blank is paid in no
            // month at all, so an empty selector is never the right default.
            mois: p.mois ?? p.prime_mois ?? (p.one_shot ? new Date().getMonth() + 1 : null),
            annee: p.annee ?? p.prime_annee ?? (p.one_shot ? new Date().getFullYear() : null),
            one_shot: p.one_shot ?? false,
          }))
        );
        setEmployeeCotisations(
          (response.cotisations_sociales || []).map((c: EmployeeCotisationSociale) => ({
            id: `existing-${c.id}`,
            cotisation_id: c.cotisation_id,
            cotisation_nom: c.cotisation_nom || '',
            cotisation_taux: Number(c.cotisation_taux) || 0,
          }))
        );
      } catch (error) {
        console.error('Error loading employee primes:', error);
      } finally {
        setLoadingPrimes(false);
      }
    };
    loadEmployeePrimes();
  }, [employeeId]);

  // Recalculate payroll when inputs change
  useEffect(() => {
    if (loadingConfig || loadingPrimes) return;

    // Use employee's cotisations for calculation
    const cotisationsForCalc = employeeCotisations
      .filter(c => c.cotisation_id)
      .map(c => ({ nom: c.cotisation_nom, taux: Number(c.cotisation_taux) || 0 }));

    // Ensure all values are proper numbers to avoid string concatenation
    const baseSalary = Number(salaireBase) || 0;

    // Only the primes that are live THIS month count, exactly as the paie
    // decides it (primeAppliesToMonth / prime_applies): a prime arrêtée, one
    // that starts later, or a one-shot paid in another month is still listed
    // above so HR can manage it, but it must not inflate the récapitulatif.
    const now = new Date();
    const moisCourant = now.getMonth() + 1;
    const anneeCourante = now.getFullYear();
    const estActive = (p: PrimeRowExoneree | PrimeRowNonExoneree) =>
      primeAppliesToMonth(p, moisCourant, anneeCourante);

    const exonereesForCalc = primesExonerees
      .filter(p => p.prime_id && p.montant > 0 && estActive(p))
      .map(p => ({ nom: p.prime_nom, montant: Number(p.montant) || 0 }));
    const nonExonereesForCalc = primesNonExonerees
      .filter(p => p.prime_id && p.montant > 0 && estActive(p))
      .map(p => ({ nom: p.prime_nom, montant: Number(p.montant) || 0 }));

    // Seniority as of today — the fiche is a "current situation" view, unlike
    // Base de Calcul which rates each month with the seniority held that month.
    const tauxAnc = tauxAncienneteAt(dateEmbauche);

    const result = calculatePayroll(
      baseSalary,
      exonereesForCalc,
      nonExonereesForCalc,
      cotisationsForCalc,
      nbrEnfants,
      situationFamiliale,
      tauxAnc
    );
    setPayroll(result);
  }, [salaireBase, primesExonerees, primesNonExonerees, employeeCotisations, nbrEnfants, situationFamiliale, dateEmbauche, loadingConfig, loadingPrimes]);

  // Update all calculated payroll fields when payroll changes
  useEffect(() => {
    if (payroll) {
      // Pass all calculated values to parent for saving
      onSalaireChange('salaire_net', payroll.netAPayer);
      onSalaireChange('salaire_brut_global', payroll.salaireBrutGlobal);
      onSalaireChange('salaire_brut_imposable', payroll.salaireBrutImposable);
      onSalaireChange('total_cotisations', payroll.totalCotisations);
      onSalaireChange('frais_professionnel', payroll.fraisProfessionnel);
      onSalaireChange('salaire_net_imposable', payroll.salaireNetImposable);
      onSalaireChange('ir_brut', payroll.irBrut);
      onSalaireChange('deduction_charge_famille', payroll.deductionChargeFamille);
      onSalaireChange('ir_net', payroll.ir);
    }
  }, [payroll, onSalaireChange]);

  // Notify parent of primes changes
  const notifyPrimesChange = useCallback(() => {
    const validExonerees = primesExonerees
      .filter(p => p.prime_id && p.montant > 0)
      .map(p => ({
        id: p.db_id ?? null,
        prime_id: p.prime_id!,
        montant: p.montant,
        date_debut: p.date_debut ?? null,
        date_fin: p.date_fin ?? null,
      }));
    const validNonExonerees = primesNonExonerees
      .filter(p => p.prime_id && p.montant > 0)
      .map(p => ({
        id: p.db_id ?? null,
        prime_id: p.prime_id!,
        montant: p.montant,
        nombre_jours: p.nombre_jours ?? null,
        mois: p.mois ?? null,
        annee: p.annee ?? null,
        date_debut: p.date_debut ?? null,
        date_fin: p.date_fin ?? null,
      }));
    const validCotisations = employeeCotisations
      .filter(c => c.cotisation_id)
      .map(c => ({ cotisation_id: c.cotisation_id! }));
    onPrimesChange(validExonerees, validNonExonerees, validCotisations);
  }, [primesExonerees, primesNonExonerees, employeeCotisations, onPrimesChange]);

  useEffect(() => {
    notifyPrimesChange();
  }, [notifyPrimesChange]);

  // Add new prime row
  const handleAddPrimeExoneree = () => {
    setPrimesExonerees([...primesExonerees, { id: generateId(), prime_id: null, prime_nom: '', montant: 0, date_debut: currentMonthISO(), date_fin: null }]);
  };

  const handleAddPrimeNonExoneree = () => {
    setPrimesNonExonerees([...primesNonExonerees, { id: generateId(), prime_id: null, prime_nom: '', montant: 0, date_debut: currentMonthISO(), date_fin: null }]);
  };

  // Remove prime row
  const handleRemovePrimeExoneree = (id: string) => {
    setPrimesExonerees(primesExonerees.filter(p => p.id !== id));
  };

  const handleRemovePrimeNonExoneree = (id: string) => {
    setPrimesNonExonerees(primesNonExonerees.filter(p => p.id !== id));
  };

  // Update prime row
  const handlePrimeExonereeChange = (id: string, field: 'prime_id' | 'montant', value: any) => {
    setPrimesExonerees(primesExonerees.map(p => {
      if (p.id !== id) return p;
      if (field === 'prime_id') {
        const selected = availablePrimesExonerees.find(ap => ap.id === value);
        return {
          ...p,
          prime_id: value,
          prime_nom: selected?.nom || '',
          one_shot: selected?.one_shot ?? false,
          // The fiche can't edit an exonérée's period, so the definition's is
          // the one that applies — same fallback as prime_applies().
          mois: selected?.one_shot ? selected?.mois ?? null : null,
          annee: selected?.one_shot ? selected?.annee ?? null : null,
        };
      }
      return { ...p, [field]: value };
    }));
  };

  // A day of leave is worth salaire_base / 26 — the same daily rate the payslip
  // already uses for the CONGE and JOUR FERIE lines.
  const tauxJournalier = (Number(salaireBase) || 0) / 26;

  // Une ligne = une période. Désactiver pose une date de fin ; pour reprendre la
  // prime plus tard on ajoute une NOUVELLE ligne, on ne rouvre pas celle-ci —
  // sinon les mois d'interruption seraient payés rétroactivement.
  const setExoneree = (id: string, patch: Partial<PrimeRowExoneree>) =>
    setPrimesExonerees(rows => rows.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const setNonExoneree = (id: string, patch: Partial<PrimeRowNonExoneree>) =>
    setPrimesNonExonerees(rows => rows.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const handlePrimeNonExonereeChange = (
    id: string,
    field: 'prime_id' | 'montant' | 'nombre_jours' | 'mois' | 'annee',
    value: any,
  ) => {
    setPrimesNonExonerees(primesNonExonerees.map(p => {
      if (p.id !== id) return p;
      if (field === 'prime_id') {
        const selected = availablePrimesNonExonerees.find(ap => ap.id === value);
        // Switching to a prime that isn't day-based drops any stale day count.
        const now = new Date();
        return {
          ...p,
          prime_id: value,
          prime_nom: selected?.nom || '',
          nombre_jours: selected?.base_jours ? p.nombre_jours ?? null : null,
          // Default a one-shot prime to the current month rather than leaving it
          // blank — a one-shot with no period is paid in no month at all.
          mois: selected?.one_shot ? (p.mois ?? now.getMonth() + 1) : null,
          annee: selected?.one_shot ? (p.annee ?? now.getFullYear()) : null,
          one_shot: selected?.one_shot ?? false,
        };
      }
      if (field === 'mois' || field === 'annee') {
        return { ...p, [field]: value === '' || value === null ? null : Number(value) };
      }
      if (field === 'nombre_jours') {
        const jours = value === '' || value === null ? null : Number(value);
        // Derive the montant, but leave it editable afterwards: an unusual
        // daily rate can still be forced by typing over it.
        return {
          ...p,
          nombre_jours: jours,
          montant: jours === null ? p.montant : Math.round(jours * tauxJournalier * 100) / 100,
        };
      }
      return { ...p, [field]: value };
    }));
  };

  // Cotisation handlers
  const handleAddCotisation = () => {
    setEmployeeCotisations([...employeeCotisations, { id: generateId(), cotisation_id: null, cotisation_nom: '', cotisation_taux: 0 }]);
  };

  const handleRemoveCotisation = (id: string) => {
    setEmployeeCotisations(employeeCotisations.filter(c => c.id !== id));
  };

  const handleCotisationChange = (id: string, value: number | null) => {
    setEmployeeCotisations(employeeCotisations.map(c => {
      if (c.id !== id) return c;
      const selected = availableCotisations.find(ac => ac.id === value);
      return {
        ...c,
        cotisation_id: value,
        cotisation_nom: selected?.nom || '',
        cotisation_taux: Number(selected?.taux) || 0
      };
    }));
  };

  // Format currency
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '0,00 DH';
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value) + ' DH';
  };

  if (loadingConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: HR_COLOR }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Two-column layout to minimize vertical scrolling */}
      <Grid container spacing={3}>
        {/* LEFT COLUMN - Salary Info + Primes */}
        <Grid item xs={12} md={6}>
          {/* Informations Salariales Section */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: HR_COLOR, mb: 2 }}>
              Informations Salariales
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <PriceInput
                  label="Salaire de Base"
                  value={salaireBase}
                  onChange={(value) => onSalaireChange('salaire_base', value)}
                  currency="DH"
                  disabled={isReadOnly}
                  error={hasError('salaire_base')}
                  helperText={getErrorText('salaire_base')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PriceInput
                  label="Déclaration Journalière"
                  value={declarationJournaliere}
                  onChange={(value) => onSalaireChange('declaration_journaliere', value)}
                  currency=""
                  disabled={isReadOnly}
                  error={hasError('declaration_journaliere')}
                  helperText={getErrorText('declaration_journaliere')}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Primes Non Exonerees Section */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: HR_COLOR }}>
                Primes Non Exonérées (Imposables)
              </Typography>
              {!isReadOnly && (
                <Tooltip title="Ajouter">
                  <IconButton size="small" onClick={handleAddPrimeNonExoneree} sx={{ color: HR_COLOR }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {primesNonExonerees.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Aucune prime. {!isReadOnly && 'Cliquez sur + pour ajouter.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {primesNonExonerees.map((row) => {
                  // Filter out already selected primes (except current row's selection)
                  const selectedIds = primesNonExonerees
                    .filter(p => p.id !== row.id && p.prime_id)
                    .map(p => p.prime_id);
                  // Idem : une ligne par période, donc une même prime peut
                  // apparaître plusieurs fois.
                  const filteredOptions = availablePrimesNonExonerees;
                  const selectedPrime = availablePrimesNonExonerees.find(p => p.id === row.prime_id);
                  const isBaseJours = Boolean(selectedPrime?.base_jours);
                  const isOneShot = Boolean(selectedPrime?.one_shot);
                  return (
                  <Box
                    key={row.id}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...PRIME_ROW_SX }}
                  >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Autocomplete
                      size="small"
                      options={filteredOptions}
                      getOptionLabel={(option) => option.nom}
                      value={selectedPrime || null}
                      onChange={(_, newValue) => handlePrimeNonExonereeChange(row.id, 'prime_id', newValue?.id || null)}
                      disabled={isReadOnly}
                      sx={{ flex: 1, minWidth: 150 }}
                      renderInput={(params) => <TextField {...params} placeholder="Prime..." size="small" />}
                    />
                    {isBaseJours && (
                      <>
                        <TextField
                          size="small"
                          type="number"
                          label="Jours"
                          value={row.nombre_jours ?? ''}
                          onChange={(e) => handlePrimeNonExonereeChange(row.id, 'nombre_jours', e.target.value)}
                          disabled={isReadOnly}
                          inputProps={{ min: 0, step: 0.5 }}
                          sx={{ width: 90 }}
                        />
                        <Tooltip title={`Taux journalier = salaire de base / 26`}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', minWidth: 62 }}>
                            × {tauxJournalier.toFixed(2)}
                          </Typography>
                        </Tooltip>
                      </>
                    )}
                    <Box sx={{ width: 145 }}>
                      <PriceInput
                        value={row.montant}
                        onChange={(value) => handlePrimeNonExonereeChange(row.id, 'montant', parseFloat(String(value)) || 0)}
                        currency="DH"
                        disabled={isReadOnly || !row.prime_id}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {/* Supprimer efface la prime de TOUS les mois, y compris ceux
                        déjà payés. On ne le propose donc que sur une ligne pas
                        encore enregistrée — une saisie qu'on corrige avant de
                        valider. Pour arrêter une prime en cours, on bascule
                        l'interrupteur : les mois déjà versés restent acquis. */}
                    {!isReadOnly && !row.db_id && (
                      <Tooltip title="Retirer cette ligne (non enregistrée)">
                        <IconButton size="small" onClick={() => handleRemovePrimeNonExoneree(row.id)} sx={{ color: '#f44336' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {isOneShot && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        Payée sur&nbsp;:
                      </Typography>
                      <TextField
                        size="small"
                        select
                        label="Mois"
                        value={row.mois ?? ''}
                        onChange={(e) => handlePrimeNonExonereeChange(row.id, 'mois', e.target.value)}
                        disabled={isReadOnly}
                        sx={{ width: 145 }}
                      >
                        {MOIS_NOMS.map((m, i) => (
                          <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        type="number"
                        label="Année"
                        value={row.annee ?? ''}
                        onChange={(e) => handlePrimeNonExonereeChange(row.id, 'annee', e.target.value)}
                        disabled={isReadOnly}
                        inputProps={{ min: 2020, max: 2099 }}
                        sx={{ width: 110 }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        prime ponctuelle — payée ce mois-là uniquement
                      </Typography>
                    </Box>
                  )}
                  {/* Fenêtre de validité : réservée aux primes récurrentes. Une
                      prime ponctuelle porte déjà son mois unique ci-dessus, et
                      prime_applies() ignore la fenêtre dans ce cas — l'afficher
                      ferait croire à deux réglages concurrents. */}
                  {!isOneShot && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 0.5 }}>
                    <TextField
                      size="small"
                      type="month"
                      label="Début"
                      value={monthInput(row.date_debut)}
                      onChange={(e) => setNonExoneree(row.id, {
                        date_debut: e.target.value ? `${e.target.value}-01` : null,
                      })}
                      disabled={isReadOnly}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 165 }}
                    />
                    <FormControlLabel
                      sx={{ ml: 0.5 }}
                      control={
                        <Switch
                          size="small"
                          checked={!row.date_fin}
                          disabled={isReadOnly}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNonExoneree(row.id, {
                            date_fin: e.target.checked ? null : endOfMonthISO(),
                          })}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ color: row.date_fin ? 'text.secondary' : 'success.main' }}>
                          {row.date_fin ? `Arrêtée fin ${monthInput(row.date_fin)}` : 'Active'}
                        </Typography>
                      }
                    />
                    {!row.date_debut && (
                      <Typography variant="caption" sx={{ color: 'warning.main' }}>
                        sans date de début — s'applique à tous les mois passés
                      </Typography>
                    )}
                  </Box>
                  )}
                  </Box>
                  );
                })}
              </Box>
            )}
          </Paper>

          {/* Primes Exonerees Section */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: HR_COLOR }}>
                Primes Exonérées (Non imposables)
              </Typography>
              {!isReadOnly && (
                <Tooltip title="Ajouter">
                  <IconButton size="small" onClick={handleAddPrimeExoneree} sx={{ color: HR_COLOR }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {primesExonerees.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Aucune prime. {!isReadOnly && 'Cliquez sur + pour ajouter.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {primesExonerees.map((row) => {
                  // Filter out already selected primes (except current row's selection)
                  const selectedIds = primesExonerees
                    .filter(p => p.id !== row.id && p.prime_id)
                    .map(p => p.prime_id);
                  // Plus de dédoublonnage : une prime arrêtée puis reprise
                  // occupe deux lignes, chacune avec sa période.
                  const filteredOptions = availablePrimesExonerees;
                  const selectedExo = availablePrimesExonerees.find(p => p.id === row.prime_id);
                  const isOneShot = Boolean(selectedExo?.one_shot);
                  return (
                  <Box key={row.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...PRIME_ROW_SX }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Autocomplete
                      size="small"
                      options={filteredOptions}
                      getOptionLabel={(option) => option.nom}
                      value={availablePrimesExonerees.find(p => p.id === row.prime_id) || null}
                      onChange={(_, newValue) => handlePrimeExonereeChange(row.id, 'prime_id', newValue?.id || null)}
                      disabled={isReadOnly}
                      sx={{ flex: 1, minWidth: 150 }}
                      renderInput={(params) => <TextField {...params} placeholder="Prime..." size="small" />}
                    />
                    <Box sx={{ width: 145 }}>
                      <PriceInput
                        value={row.montant}
                        onChange={(value) => handlePrimeExonereeChange(row.id, 'montant', parseFloat(String(value)) || 0)}
                        currency="DH"
                        disabled={isReadOnly || !row.prime_id}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    {/* Supprimer efface la prime de TOUS les mois, y compris ceux
                        déjà payés. On ne le propose donc que sur une ligne pas
                        encore enregistrée — une saisie qu'on corrige avant de
                        valider. Pour arrêter une prime en cours, on bascule
                        l'interrupteur : les mois déjà versés restent acquis. */}
                    {!isReadOnly && !row.db_id && (
                      <Tooltip title="Retirer cette ligne (non enregistrée)">
                        <IconButton size="small" onClick={() => handleRemovePrimeExoneree(row.id)} sx={{ color: '#f44336' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {/* Fenêtre de validité : réservée aux primes récurrentes. Une
                      prime ponctuelle porte déjà son mois unique ci-dessus, et
                      prime_applies() ignore la fenêtre dans ce cas — l'afficher
                      ferait croire à deux réglages concurrents. */}
                  {!isOneShot && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 0.5 }}>
                    <TextField
                      size="small"
                      type="month"
                      label="Début"
                      value={monthInput(row.date_debut)}
                      onChange={(e) => setExoneree(row.id, {
                        date_debut: e.target.value ? `${e.target.value}-01` : null,
                      })}
                      disabled={isReadOnly}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 165 }}
                    />
                    <FormControlLabel
                      sx={{ ml: 0.5 }}
                      control={
                        <Switch
                          size="small"
                          checked={!row.date_fin}
                          disabled={isReadOnly}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExoneree(row.id, {
                            date_fin: e.target.checked ? null : endOfMonthISO(),
                          })}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ color: row.date_fin ? 'text.secondary' : 'success.main' }}>
                          {row.date_fin ? `Arrêtée fin ${monthInput(row.date_fin)}` : 'Active'}
                        </Typography>
                      }
                    />
                    {!row.date_debut && (
                      <Typography variant="caption" sx={{ color: 'warning.main' }}>
                        sans date de début — s'applique à tous les mois passés
                      </Typography>
                    )}
                  </Box>
                  )}
                  </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN - Cotisations + Payroll Summary */}
        <Grid item xs={12} md={6}>
          {/* Cotisations Sociales Section */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: HR_COLOR }}>
                Cotisations Sociales
              </Typography>
              {!isReadOnly && (
                <Tooltip title="Ajouter">
                  <IconButton size="small" onClick={handleAddCotisation} sx={{ color: HR_COLOR }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {employeeCotisations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Aucune cotisation. {!isReadOnly && 'Cliquez sur + pour ajouter.'}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {employeeCotisations.map((row) => {
                  // Filter out already selected cotisations (except current row's selection)
                  const selectedIds = employeeCotisations
                    .filter(c => c.id !== row.id && c.cotisation_id)
                    .map(c => c.cotisation_id);
                  const filteredOptions = availableCotisations.filter(
                    c => !selectedIds.includes(c.id) || c.id === row.cotisation_id
                  );
                  return (
                  <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Autocomplete
                      size="small"
                      options={filteredOptions}
                      getOptionLabel={(option) => `${option.nom} (${Number(option.taux).toFixed(2)}%)`}
                      value={availableCotisations.find(c => c.id === row.cotisation_id) || null}
                      onChange={(_, newValue) => handleCotisationChange(row.id, newValue?.id || null)}
                      disabled={isReadOnly}
                      sx={{ flex: 1 }}
                      renderInput={(params) => <TextField {...params} placeholder="Cotisation..." size="small" />}
                    />
                    {!isReadOnly && (
                      <IconButton size="small" onClick={() => handleRemoveCotisation(row.id)} sx={{ color: '#f44336' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  );
                })}
              </Box>
            )}
          </Paper>

          {/* Payroll Calculation Summary */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: HR_COLOR, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              Récapitulatif de Paie
            </Typography>

            {payroll && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.25, fontWeight: 600, fontSize: '0.7rem', color: '#666' }}>Élément</TableCell>
                      <TableCell sx={{ py: 0.25, fontWeight: 600, fontSize: '0.7rem', color: '#666' }}>Calcul</TableCell>
                      <TableCell align="right" sx={{ py: 0.25, fontWeight: 600, fontSize: '0.7rem', color: '#666' }}>Montant</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>A - Salaire de Base</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>—</TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>{formatCurrency(payroll.salaireBase)}</TableCell>
                    </TableRow>
                    {/* Primes Non Exonérées Section */}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>B - Primes Non Exonérées</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}></TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}></TableCell>
                    </TableRow>
                    {payroll.primesNonExonereesDetails.map((prime, index) => (
                      <TableRow key={`pne-${index}`}>
                        <TableCell sx={{ py: 0.25, border: 0, fontSize: '0.7rem', pl: 3, color: '#666' }}>
                          • {prime.nom}
                        </TableCell>
                        <TableCell sx={{ py: 0.25, border: 0, fontSize: '0.65rem', color: '#888' }}>—</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, border: 0, fontSize: '0.7rem' }}>
                          {formatCurrency(prime.montant)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600, pl: 2 }}>Total Primes Non Exonérées</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>Σ primes</TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>{formatCurrency(payroll.totalPrimesNonExonerees)}</TableCell>
                    </TableRow>

                    {/* Primes Exonérées Section */}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>C - Primes Exonérées</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}></TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}></TableCell>
                    </TableRow>
                    {payroll.primesExonereesDetails.map((prime, index) => (
                      <TableRow key={`pe-${index}`}>
                        <TableCell sx={{ py: 0.25, border: 0, fontSize: '0.7rem', pl: 3, color: '#666' }}>
                          • {prime.nom}
                        </TableCell>
                        <TableCell sx={{ py: 0.25, border: 0, fontSize: '0.65rem', color: '#888' }}>—</TableCell>
                        <TableCell align="right" sx={{ py: 0.25, border: 0, fontSize: '0.7rem' }}>
                          {formatCurrency(prime.montant)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600, pl: 2 }}>Total Primes Exonérées</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>Σ primes</TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>{formatCurrency(payroll.totalPrimesExonerees)}</TableCell>
                    </TableRow>
                    {!!payroll.tauxAnciennete && (
                      <TableRow>
                        <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600, color: 'success.main' }}>
                          Prime d'Ancienneté ({((payroll.tauxAnciennete || 0) * 100).toFixed(0)}%)
                        </TableCell>
                        <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>
                          SB={payroll.salaireBase.toLocaleString()} × {((payroll.tauxAnciennete || 0) * 100).toFixed(0)}%
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600, color: 'success.main' }}>
                          +{formatCurrency(payroll.primeAnciennete || 0)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>D - Salaire Brut Global</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>
                        A={payroll.salaireBase.toLocaleString()} + B={payroll.totalPrimesNonExonerees.toLocaleString()} + C={payroll.totalPrimesExonerees.toLocaleString()}
                        {!!payroll.tauxAnciennete && ` + Anc=${(payroll.primeAnciennete || 0).toLocaleString()}`}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>{formatCurrency(payroll.salaireBrutGlobal)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>E - Salaire Brut Imposable</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>
                        D={payroll.salaireBrutGlobal.toLocaleString()} - C={payroll.totalPrimesExonerees.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>{formatCurrency(payroll.salaireBrutImposable)}</TableCell>
                    </TableRow>

                    {/* Cotisations Section */}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>F - Cotisations Sociales</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}></TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}></TableCell>
                    </TableRow>
                    {/* Cotisations Sub-items */}
                    {payroll.cotisationsDetails.map((cotisation, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ py: 0.25, border: 0, fontSize: '0.7rem', pl: 3, color: '#666' }}>
                          • {cotisation.nom}
                        </TableCell>
                        <TableCell sx={{ py: 0.25, border: 0, fontSize: '0.65rem', color: '#888' }}>
                          {cotisation.isCNSS
                            ? `${cotisation.baseForCalculation.toLocaleString()} × ${cotisation.taux.toFixed(2)}%`
                            : `E × ${cotisation.taux.toFixed(2)}%`}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.25, border: 0, fontSize: '0.7rem', color: '#f44336' }}>
                          -{formatCurrency(cotisation.montant)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Cotisations Total */}
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600, pl: 2 }}>Total Cotisations</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>
                        Σ ({payroll.cotisationsDetails.reduce((sum, c) => sum + c.taux, 0).toFixed(2)}%)
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, color: '#f44336', fontSize: '0.75rem', fontWeight: 600 }}>-{formatCurrency(payroll.totalCotisations)}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>G - Frais Professionnel</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>
                        MIN(E={payroll.salaireBrutImposable.toLocaleString()} × {(payroll.fraisProfessionnelRate * 100).toFixed(0)}%, {payroll.fraisProfessionnelMax.toLocaleString()})
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>{formatCurrency(payroll.fraisProfessionnel)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>H - Salaire Net Imposable</TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.7rem', color: '#666' }}>
                        E={payroll.salaireBrutImposable.toLocaleString()} - F={payroll.totalCotisations.toLocaleString()} - G={payroll.fraisProfessionnel.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem', fontWeight: 600 }}>{formatCurrency(payroll.salaireNetImposable)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>
                        I - IR Brut
                        {payroll.irBracket && <Typography component="span" sx={{ fontSize: '0.65rem', color: '#666' }}> ({payroll.irBracket.rate * 100}%)</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.65rem', color: '#666' }}>
                        {payroll.irBracket
                          ? `(H=${payroll.salaireNetImposable.toLocaleString()} × ${payroll.irBracket.rate * 100}%) - ${payroll.irBracket.deduction.toLocaleString()}`
                          : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>{formatCurrency(payroll.irBrut)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>
                        J - Déduction Charge Famille
                      </TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.65rem', color: '#666' }}>
                        {payroll.nbrPersonnesACharge} pers. × 41,67 DH (max 250)
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>{formatCurrency(payroll.deductionChargeFamille)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.75rem' }}>
                        K - IR Net
                      </TableCell>
                      <TableCell sx={{ py: 0.5, border: 0, fontSize: '0.65rem', color: '#666' }}>
                        I={payroll.irBrut.toLocaleString()} - J={payroll.deductionChargeFamille.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5, border: 0, color: payroll.ir < 0 ? '#f44336' : 'inherit', fontSize: '0.75rem' }}>{formatCurrency(payroll.ir)}</TableCell>
                    </TableRow>

                    {/* NET TO PAY */}
                    <TableRow sx={{ backgroundColor: HR_COLOR }}>
                      <TableCell sx={{ py: 0.75, fontWeight: 700, color: 'white', fontSize: '0.8rem' }}>NET A PAYER</TableCell>
                      <TableCell sx={{ py: 0.75, color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                        D={payroll.salaireBrutGlobal.toLocaleString()} - F={payroll.totalCotisations.toLocaleString()} - K={payroll.ir.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75, fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>{formatCurrency(payroll.netAPayer)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaieTab;
